/**
 * 部件标签栏状态管理。
 *
 * 职责：
 * 1. 管理导入模型的部件标签列表（每个 GLB 文件对应一个标签）；
 * 2. 跟踪当前激活的标签；
 * 3. 提供增删切换操作。
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { FileInfo } from '@puppycad/proto'
import {
  buildImportedModelTabs,
  type ImportedModelTabDescriptor,
  type ImportedModelTabType,
} from '@puppycad/cad-core'
import type { GlbNodeIconType } from '@puppycad/cad-core'

export const usePartTabsStore = defineStore('part-tabs', () => {
  /** 所有标签列表 */
  const tabs = ref<ImportedModelTabDescriptor[]>([])
  /** 当前激活标签 ID */
  const activeTabId = ref<string | null>(null)

  /** 当前激活的标签描述 */
  const activeTab = computed<ImportedModelTabDescriptor | null>(() => {
    if (!activeTabId.value) return null
    return tabs.value.find((tab) => tab.id === activeTabId.value) ?? null
  })

  /** 标签数量 */
  const tabCount = computed(() => tabs.value.length)

  /**
   * 根据 GLB 文件列表初始化标签。
   * 将每个文件转换为一个标签描述。
   *
   * @param glbFiles GLB 文件列表。
   * @param projectFiles 完整项目文件列表，用于识别默认打开的主装配标签。
   */
  function initFromGlbFiles(glbFiles: FileInfo[], projectFiles: FileInfo[] = glbFiles): void {
    tabs.value = buildImportedModelTabs(glbFiles, projectFiles)
    // 默认激活第一个标签
    activeTabId.value = tabs.value.length > 0 ? tabs.value[0].id : null
  }

  /**
   * 添加一个标签。
   *
   * @param descriptor 标签描述。
   */
  function addTab(descriptor: ImportedModelTabDescriptor): void {
    const existing = tabs.value.find((tab) => tab.id === descriptor.id)
    if (existing) {
      return
    }
    tabs.value = [...tabs.value, descriptor]
  }

  /**
   * 移除指定标签。
   * 如果移除的是当前激活标签，自动切换到前一个或后一个标签。
   *
   * @param tabId 要移除的标签 ID。
   */
  function removeTab(tabId: string): void {
    const index = tabs.value.findIndex((tab) => tab.id === tabId)
    if (index === -1) {
      return
    }

    const nextTabs = tabs.value.filter((tab) => tab.id !== tabId)
    tabs.value = nextTabs

    // 如果移除的是当前标签，需要切换
    if (activeTabId.value === tabId) {
      if (nextTabs.length === 0) {
        activeTabId.value = null
      } else {
        // 优先选择同位置，否则选最后一个
        const nextIndex = Math.min(index, nextTabs.length - 1)
        activeTabId.value = nextTabs[nextIndex].id
      }
    }
  }

  /**
   * 切换到指定标签。
   *
   * @param tabId 目标标签 ID。
   */
  function switchTab(tabId: string): void {
    const target = tabs.value.find((tab) => tab.id === tabId)
    if (!target) {
      return
    }
    activeTabId.value = tabId
  }

  /**
   * 更新标签的加载状态。
   *
   * @param tabId   标签 ID。
   * @param loading 是否正在加载。
   */
  function setTabLoading(tabId: string, loading: boolean): void {
    tabs.value = tabs.value.map((tab) =>
      tab.id === tabId ? { ...tab, loading } : tab,
    )
  }

  /**
   * 更新标签的类型与图标信息。
   *
   * @param tabId    标签 ID。
   * @param type     标签类型。
   * @param iconType 图标类型。
   */
  function updateTabType(
    tabId: string,
    type: ImportedModelTabType | null,
    iconType: GlbNodeIconType | null,
  ): void {
    tabs.value = tabs.value.map((tab) =>
      tab.id === tabId ? { ...tab, type, iconType } : tab,
    )
  }

  /**
   * 更新标签的 scopeObjectId（装配体展开后用于定位子装配）。
   *
   * @param tabId         标签 ID。
   * @param scopeObjectId 作用域对象 ID。
   */
  function updateTabScopeObjectId(tabId: string, scopeObjectId: string | null): void {
    tabs.value = tabs.value.map((tab) =>
      tab.id === tabId ? { ...tab, scopeObjectId } : tab,
    )
  }

  /**
   * 清空所有标签状态。
   */
  function reset(): void {
    tabs.value = []
    activeTabId.value = null
  }

  return {
    tabs,
    activeTabId,
    activeTab,
    tabCount,
    initFromGlbFiles,
    addTab,
    removeTab,
    switchTab,
    setTabLoading,
    updateTabType,
    updateTabScopeObjectId,
    reset,
  }
})
